import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { gbp, api } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function Cart() {
  const { cart, cartKey, updateQty, removeFromCart, cartSubtotal } = useStore();
  const FREE = 40;
  const remaining = Math.max(0, FREE - cartSubtotal);
  const total = cartSubtotal;
  const [loading, setLoading] = useState(false);

  const goToCheckout = async () => {
    const lines = cart.filter((i) => i.variant_id).map((i) => ({ variant_id: i.variant_id, quantity: i.qty }));
    if (!lines.length) { toast.error("Your items can't be checked out. Please re-add them."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/shopify/checkout", { lines });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout. Please try again."); setLoading(false); }
  };

  if (cart.length === 0) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <Seo title="Basket" />
      <ShoppingBag className="w-12 h-12 mx-auto text-brand-pink mb-4" />
      <h1 className="font-serif-display text-4xl mb-3">Your basket is empty</h1>
      <p className="text-muted-foreground mb-6">Let's find something cosy and handmade.</p>
      <Link to="/shop" className="inline-block bg-brand-ink text-white px-8 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="empty-shop-link">Shop the collection</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Basket" />
      <h1 className="font-serif-display text-4xl sm:text-5xl mb-2">Your basket</h1>
      {remaining > 0
        ? <p className="text-sm text-muted-foreground mb-8">Add {gbp(remaining)} more for <span className="text-brand-pink">free UK delivery</span>.</p>
        : <p className="text-sm text-[#2D6A4F] mb-8">You've unlocked free UK delivery! 🎉</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
        <div className="space-y-5" data-testid="cart-items">
          {cart.map((it) => {
            const key = cartKey(it);
            return (
              <div key={key} className="flex gap-4 border-b border-brand-border pb-5" data-testid={`cart-item-${it.id}`}>
                <img src={it.image} alt={it.title} className="w-24 h-28 object-cover rounded-sm" />
                <div className="flex-1">
                  <div className="flex justify-between gap-3">
                    <div>
                      <h3 className="font-serif-display text-xl">{it.title}</h3>
                      {it.type === "bundle" && <span className="text-xs uppercase tracking-luxe text-brand-pink">Bundle</span>}
                      {it.colour && <p className="text-sm text-muted-foreground">Colour: {it.colour}</p>}
                      {it.selections && <p className="text-xs text-muted-foreground mt-1">{it.selections.map((s) => `${s.title}${s.colour ? ` (${s.colour})` : ""}`).join(", ")}</p>}
                    </div>
                    <button onClick={() => removeFromCart(key)} className="text-muted-foreground hover:text-destructive h-fit" data-testid={`remove-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-brand-border rounded-sm">
                      <button onClick={() => updateQty(key, it.qty - 1)} className="px-2.5 py-2 hover:bg-brand-blush"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="px-3 text-sm">{it.qty}</span>
                      <button onClick={() => updateQty(key, it.qty + 1)} className="px-2.5 py-2 hover:bg-brand-blush"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <span className="font-medium">{gbp(it.unit_price * it.qty)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-brand-blush/40 rounded-sm p-7 h-fit">
          <h3 className="font-serif-display text-2xl mb-5">Summary</h3>
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Subtotal</span><span data-testid="cart-subtotal">{gbp(cartSubtotal)}</span></div>
          <div className="flex justify-between text-sm mb-4"><span className="text-muted-foreground">Delivery</span><span>Calculated at checkout</span></div>
          <div className="border-t border-brand-border pt-4 flex justify-between font-medium mb-6"><span>Total</span><span data-testid="cart-total">{gbp(total)}</span></div>
          <button onClick={goToCheckout} disabled={loading} className="flex items-center justify-center gap-2 w-full bg-brand-ink text-white py-3.5 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors disabled:opacity-50" data-testid="checkout-button">{loading ? "Redirecting…" : <>Checkout <ArrowRight className="w-4 h-4" /></>}</button>
          <p className="text-[11px] text-muted-foreground text-center mt-2">Secure checkout & payment handled by Shopify</p>
          <Link to="/shop" className="block text-center text-xs uppercase tracking-luxe mt-4 underline">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
